import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';
import {
  getApiPatientAuthorizationGetByIdPatientAuthorizationId,
  getApiPatientAuthorizationGetByInsuranceInsuranceId,
  getApiPatientAuthorizationGetByPatientPatientId,
  postApiPatientAuthorizationSave,
  deleteApiPatientAuthorizationDeletePatientAuthorizationId,
} from 'endpoints';
import type {
  GetApiPatientAuthorizationGetByPatientPatientIdParams,
  PatientAuthorizationListItemDTO,
  PatientAuthorizationListItemDTOPaginatedResponseDTO,
  PatientAuthorizationResponseDTO,
  PatientAuthorizationSaveRequestDTO,
} from 'dtos';

type QueryOptions<T> = Omit<
  UseQueryOptions<T, Error, T, readonly unknown[]>,
  'queryKey' | 'queryFn' | 'enabled'
> & { enabled?: boolean };

export const authorizationQueryKeys = {
  all: ['patient-authorizations'] as const,
  list: (
    patientId: number,
    params: GetApiPatientAuthorizationGetByPatientPatientIdParams,
  ) => [...authorizationQueryKeys.all, 'list', patientId, params] as const,
  detail: (authorizationId: number) => [
    ...authorizationQueryKeys.all,
    'detail',
    authorizationId,
  ] as const,
  insurance: (insuranceId: number) => [
    ...authorizationQueryKeys.all,
    'insurance',
    insuranceId,
  ] as const,
};

export function getPatientAuthorizations(
  patientId: number,
  params?: GetApiPatientAuthorizationGetByPatientPatientIdParams,
  options?: { signal?: AbortSignal },
): Promise<PatientAuthorizationListItemDTOPaginatedResponseDTO> {
  return getApiPatientAuthorizationGetByPatientPatientId(patientId, params, options);
}

export function useGetPatientAuthorization(
  authorizationId: number,
  options?: QueryOptions<PatientAuthorizationResponseDTO>,
): UseQueryResult<PatientAuthorizationResponseDTO, Error> {
  return useQuery<PatientAuthorizationResponseDTO, Error>({
    ...options,
    queryKey: authorizationQueryKeys.detail(authorizationId),
    queryFn: ({ signal }) => getApiPatientAuthorizationGetByIdPatientAuthorizationId(
      authorizationId,
      { signal },
    ),
    enabled: (options?.enabled ?? true) && authorizationId > 0,
  });
}

export function useGetPatientAuthorizationsByInsurance(
  insuranceId: number,
  options?: QueryOptions<PatientAuthorizationListItemDTO[]>,
): UseQueryResult<PatientAuthorizationListItemDTO[], Error> {
  return useQuery<PatientAuthorizationListItemDTO[], Error>({
    ...options,
    queryKey: authorizationQueryKeys.insurance(insuranceId),
    queryFn: ({ signal }) => getApiPatientAuthorizationGetByInsuranceInsuranceId(
      insuranceId,
      { signal },
    ),
    enabled: (options?.enabled ?? true) && insuranceId > 0,
  });
}

function useInvalidateAuthorizationQueries() {
  const queryClient = useQueryClient();

  return async () => {
    await queryClient.invalidateQueries({
      queryKey: authorizationQueryKeys.all,
    });
    // DataProvider puts its queryKey after the patient and pagination parameters.
    await queryClient.invalidateQueries({
      predicate: ({ queryKey }) => queryKey.at(-1) === authorizationQueryKeys.all[0],
    });
  };
}

export function useSavePatientAuthorization(): UseMutationResult<
  PatientAuthorizationResponseDTO,
  Error,
  PatientAuthorizationSaveRequestDTO
> {
  const invalidate = useInvalidateAuthorizationQueries();

  return useMutation<
    PatientAuthorizationResponseDTO,
    Error,
    PatientAuthorizationSaveRequestDTO
  >({
    mutationFn: (payload) => postApiPatientAuthorizationSave(payload),
    onSuccess: async () => {
      await invalidate();
    },
  });
}

export function useDeletePatientAuthorization(): UseMutationResult<
  unknown,
  Error,
  number
> {
  const invalidate = useInvalidateAuthorizationQueries();

  return useMutation<unknown, Error, number>({
    mutationFn: (authorizationId) => (
      deleteApiPatientAuthorizationDeletePatientAuthorizationId(authorizationId)
    ),
    onSuccess: async () => {
      await invalidate();
    },
  });
}
